import moment from 'moment';
import {
  LastLocationRequestType,
  LastLocationType,
  LoggedUserType,
  TrackingDateType,
  TrackingHistoryType,
  TrackLocationFilterType,
  UserType,
} from '../../utils/Types';
import { gatDistance, getFormattedTime } from '../../utils/Util';
import { CLICK_ACTIONS, DATE_FORMAT } from '../../utils/Constant';
import { Polyline } from './TrackLocationMap';
import { toast } from 'react-toastify';
import { getTrackingHistory } from '../../features/trackLocationSlice';
import { ThunkDispatch } from 'redux-thunk';
import { Location, NavigateFunction } from 'react-router-dom';
import { updateSelectedMenu } from '../../features/dashboardSlice';
import { STATE_ACCESS_ROLES } from '../../utils/RBAC';

export const getTLFilterData = (
  trackLocationFilter: TrackLocationFilterType
) => {
  const data: LastLocationRequestType = {};
  if (trackLocationFilter.employeeId)
    data.employeeId = trackLocationFilter.employeeId;
  if (trackLocationFilter.employeeName)
    data.name = trackLocationFilter.employeeName;
  if (trackLocationFilter?.role?.value)
    data.role = trackLocationFilter.role.value;
  if (trackLocationFilter?.zone?.value)
    data.zone = Number(trackLocationFilter.zone.value);
  if (trackLocationFilter?.state?.value)
    data.state = Number(trackLocationFilter.state.value);
  if (trackLocationFilter?.district?.value)
    data.district = Number(trackLocationFilter.district.value);
  if (trackLocationFilter?.city?.value)
    data.location = Number(trackLocationFilter.city.value);
  if (trackLocationFilter.status)
    data.status = trackLocationFilter.status.value;
  if (trackLocationFilter?.date) {
    data.fromDate = moment(trackLocationFilter.date).format('YYYY-MM-DD');
    data.toDate = data.fromDate;
  }
  return data;
};

export const getSortedArray = (trackHistoryList: TrackingHistoryType[]) => {
  const array: TrackingHistoryType[] = [...trackHistoryList];
  array.sort((a: any, b: any) => {
    return new Date(a.locationReceivedAt).getTime() <
      new Date(b.locationReceivedAt).getTime()
      ? -1
      : 1;
  });
  return array;
};

export const getLineAndToolTip = (trackHistoryList: TrackingHistoryType[]) => {
  const line: any[] = [];
  const toolTip: boolean[] = [];
  const array: TrackingHistoryType[] = getSortedArray(trackHistoryList);
  for (let index = 0; index < array.length; index++) {
    let item = array[index];
    if (index === 0) {
      item = { ...item, travelTime: '-' };
      item = { ...item, travelDistance: '-' };
    } else {
      const preDate = moment(array[index - 1].locationReceivedAt);
      const date = moment(array[index].locationReceivedAt);
      item = {
        ...item,
        travelTime: getFormattedTime(date.diff(preDate, 'minutes')),
      };
      item = {
        ...item,
        travelDistance: gatDistance(
          parseFloat(array[index - 1].latitude),
          parseFloat(array[index - 1].longitude),
          parseFloat(array[index].latitude),
          parseFloat(array[index].longitude)
        ),
      };
    }

    if (item && !isNaN(parseFloat(item.latitude))) {
      toolTip.push(false);
      line.push({
        lat: Number(item.latitude),
        lng: Number(item.longitude),
        ...item,
      });
    }
  }
  return {
    line,
    toolTip,
  };
};

export const getFindLocationRequest = (
  setTrackingDate: (value: TrackingDateType) => void,
  user: LoggedUserType,
  userDetails: UserType,
  trackLocationFilter?: TrackLocationFilterType
) => {
  let data: LastLocationRequestType = {};
  if (trackLocationFilter) {
    data = getTLFilterData(trackLocationFilter);
    setTrackingDate({ date: String(data.fromDate), activeDateNumber: 1 });
  } else {
    data.status = 'Logged';
    data.fromDate = moment(new Date()).format('YYYY-MM-DD');
    data.toDate = data.fromDate;
    if (
      STATE_ACCESS_ROLES.includes(user?.roleId) &&
      userDetails?.reportingZones?.length
    ) {
      data.zone = Number(userDetails?.reportingZones[0]);
    }
    setTrackingDate({ date: data.fromDate, activeDateNumber: 1 });
  }
  return data;
};
export const addPolyline = (
  line: any[],
  mapInstance: google.maps.Map | null,
  setAddedPolyline: (polyline: Polyline) => void
) => {
  try {
    if (!mapInstance) return;
    const polyline = new google.maps.Polyline({
      path: line,
      geodesic: true,
      strokeColor: '#08A650',
      strokeOpacity: 0.6,
      strokeWeight: 5,
    });
    setAddedPolyline(polyline);
    polyline.setMap(mapInstance);
  } catch (e) {}
};
export const makeZoomCenter = (
  mapInstance: google.maps.Map | null,
  employeeLastLocationList: LastLocationType[],
  locationListError: string | null
) => {
  if (employeeLastLocationList && employeeLastLocationList.length > 0) {
    mapInstance?.setCenter({
      lat: Number(employeeLastLocationList[0].latitude),
      lng: Number(employeeLastLocationList[0].longitude),
    });
    mapInstance?.setZoom(11);
  } else if (locationListError) {
    toast.dismiss();
    toast.error(locationListError);
  }
};
export const onUserMarkerClick = (
  clearData: () => void,
  dispatch: ThunkDispatch<any, any, any>,
  setShowLoader: (value: boolean) => void,
  employeeId: number,
  selectedDate?: string,
  trackingDate?: TrackingDateType
) => {
  clearData();
  const params = {
    employeeId: employeeId,
    toDate: moment(selectedDate ?? trackingDate?.date).format(
      DATE_FORMAT.YYYY_MM_DD
    ),
    fromDate: moment(selectedDate ?? trackingDate?.date).format(
      DATE_FORMAT.YYYY_MM_DD
    ),
  };
  setShowLoader(true);
  dispatch(getTrackingHistory(params));
};

export const onDataChange = (
  userDetails: UserType,
  mapInstance: google.maps.Map | null,
  location: Location,
  setTrackingDate: (value: TrackingDateType) => void,
  onProfileMarkerClick: (
    lastLocation: LastLocationType,
    selectedDate?: string
  ) => void,
  findLocationList: (value?: TrackLocationFilterType) => void,
  trackLocationFilter?: TrackLocationFilterType
) => {
  if (mapInstance) {
    const data = location?.state;
    if (data?.lastLocation) {
      setTrackingDate(data.trackingDate);
      let selectedData;
      if (data?.trackingDate?.date && data?.trackingDate?.activeDateNumber) {
        selectedData = moment(data.trackingDate.date)
          .subtract(data.trackingDate.activeDateNumber - 1, 'days')
          .format('YYYY-MM-DD');
      }

      onProfileMarkerClick(data.lastLocation, selectedData);
    } else if (userDetails?.roleid) {
      findLocationList(trackLocationFilter);
    }
  }
};
export const showTollTip = (
  setToolTipStatus: (value: boolean[] | null) => void,
  index: number,
  toolTipStatus?: boolean[] | null
) => {
  const array = toolTipStatus?.map((value, i) => {
    if (i === index) {
      return true;
    } else {
      return false;
    }
  });
  setToolTipStatus(array || null);
};
export const onTaskClick = (
  item: any,
  lastLocation: LastLocationType,
  selectedDate: string | undefined,
  activeDateNumber: number | undefined,
  dispatch: ThunkDispatch<any, any, any>,
  navigate: NavigateFunction
) => {
  navigate('/view-task', {
    state: {
      taskId: item.referenceId,
      action: CLICK_ACTIONS.SHOW_TRACK_LOCATION,
      lastLocation,
      trackingDate: { date: selectedDate, activeDateNumber },
    },
  });
  dispatch(updateSelectedMenu('view-task'));
};
export const onMarketingClick = (
  dispatch: ThunkDispatch<any, any, any>,
  navigate: NavigateFunction
) => {
  navigate('/view-activity');
  dispatch(updateSelectedMenu('view-activity'));
};
